class Cart extends HTMLElement {
  constructor() {
    super();

    this.sectionId = this.dataset[this.attributes.sectionId];

    this._onCartChanged = this.onCartChange.bind(this);
    this._onCartLoadingStarted = this.onCartLoadingStart.bind(this);
    this._onCartLoadingEnded = this.onCartLoadingEnd.bind(this);
  }

  get routes() {
    return {
      cart: `${Shopify.routes.root}cart.js`,
      cartChange: `${Shopify.routes.root}cart/change.js`
    }
  }

  get selectors() {
    return {
      section: "cart-section",
      items: "cart-items",
      totals: "cart-totals",
      notification: "[data-cart-notification]"
    }
  }

  get attributes() {
    return {
      sectionId: "sectionId"
    }
  }

  get modifiers() {
    return {
      loading: "loading"
    }
  }

  get sectionsToRender() {
    return [
      this.sectionId
    ]
  }

  connectedCallback() {
    document.addEventListener("cart:change", this._onCartChanged);

    document.addEventListener("cart:loading:start", this._onCartLoadingStarted);
    document.addEventListener("cart:loading:end", this._onCartLoadingEnded);
  }

  disconnectedCallback() {
    document.removeEventListener("cart:change", this._onCartChanged);

    document.removeEventListener("cart:loading:start", this._onCartLoadingStarted);
    document.removeEventListener("cart:loading:end", this._onCartLoadingEnded);
  }

  async onCartChange(event) {
    const { cart, cart: { sections } = {}, forceUpdate } = event?.detail ?? {};

    document.dispatchEvent(new CustomEvent("cart:loading:start"));

    await this.renderCart(cart, sections, forceUpdate);

    document.dispatchEvent(new CustomEvent("cart:loading:end"));
  }

  onCartLoadingStart() {
    this.classList.add(this.modifiers.loading);
  }

  onCartLoadingEnd() {
    this.classList.remove(this.modifiers.loading);
  }

  async renderCart(cart, sections, forceUpdate) {
    if (!cart) {
      cart = await this.getCartData();
    }

    if (!sections) {
      sections = await this.getSectionsToRenderData();
    }

    const sectionContent = new DOMParser().parseFromString(sections[this.sectionId], "text/html");

    if (forceUpdate) {
      const cartContent = sectionContent.querySelector(this.selectors.section);

      if (cartContent) {
        this.innerHTML = cartContent.innerHTML;
        this.announceCartUpdate();
      }

      return;
    }

    if (cart.item_count > 0) {
      const cartItemsContent = sectionContent.querySelector(this.selectors.items);
      const cartTotalsContent = sectionContent.querySelector(this.selectors.totals);

      const cartItemsElement = this.querySelector(this.selectors.items);
      const cartTotalsElement = this.querySelector(this.selectors.totals);

      if (cartItemsElement && cartItemsContent) {
        cartItemsElement.innerHTML = cartItemsContent.innerHTML;
      }

      if (cartTotalsElement && cartTotalsContent) {
        cartTotalsElement.innerHTML = cartTotalsContent.innerHTML;
      }

      this.announceCartUpdate();
    } else {
      const cartContent = sectionContent.querySelector(this.selectors.section);

      if (cartContent) {
        this.innerHTML = cartContent.innerHTML;
        this.announceCartUpdate();
      }
    }

    this.updateNostoRecommendations();
  }

  async getCartData() {
    return await (await fetch(this.routes.cart)).json();
  }

  async getSectionsToRenderData() {
    const sectionsToRenderQueryParam = this.sectionsToRender.map((id) => `sections[]=${encodeURIComponent(id)}`).join("&");

    return await (await fetch(
      `${Shopify.routes.root}?${sectionsToRenderQueryParam}`
    )).json();
  }

  announceCartUpdate() {
    const cartUpdateNotification = document.querySelector(this.selectors.notification);

    if (!cartUpdateNotification) {
      return;
    }

    cartUpdateNotification.innerHTML = theme.strings.cart.updated;
    cartUpdateNotification.setAttribute("aria-live", "polite");

    const cartItems = document.querySelector(this.selectors.items);

    cartItems.focus();
  }

  updateNostoRecommendations() {
    if (window.Nosto) {
      Nosto.reloadCart(["nosto-minicart"]);
    }
  }
}

if (!customElements.get("cart-section")) {
  customElements.define("cart-section", Cart);
}
